import { Link } from "react-router-dom";
import { ArrowRight, Pencil } from "lucide-react";
import { Badge, Button, ProgressBar, Skeleton, cn } from "@fox1ck-org/ui";
import { CampaignRule } from "@gen/tracker/v1/campaign_pb";
import { BranchTrigger } from "@gen/tracker/v1/common_pb";

import { WriteOnly } from "@/components/auth/write-only";
import { Aside, Panel } from "@/components/campaign/panel";
import { useStreamBreakdown } from "@/components/campaign/flow-numbers";
import { TRIGGER, campaignLanderIds, campaignOfferIds, ruleCondition } from "@/components/campaign/rules";
import { useReportParams } from "@/lib/hooks/use-report-params";
import { useT, type TKey } from "@/lib/i18n";
import { DASH, formatInt, formatPct } from "@/lib/format";
import { statsRowToNumbers } from "@/lib/services/reporting-schema-service";
import { useCampaignReport } from "@/lib/services/campaign-report-service";
import { useOffersByIds } from "@/lib/services/offer-service";
import { useLandersByIds } from "@/lib/services/lander-service";
import { useReportTimezone } from "@/lib/services/workspace-service";
import type { CardContext } from "./card";

/** The branches the board lists, in its order; «Второй шаг» only when the campaign has one. */
const EXITS: BranchTrigger[] = [BranchTrigger.BACK, BranchTrigger.IDLE, BranchTrigger.EXIT_INTENT, BranchTrigger.SCROLL_END];
/** The «branch» dimension's value of each trigger (domain.BranchTrigger). */
const TRIGGER_KEY: Partial<Record<BranchTrigger, string>> = {
  [BranchTrigger.BACK]: "back",
  [BranchTrigger.EXIT_INTENT]: "exit_intent",
  [BranchTrigger.IDLE]: "idle",
  [BranchTrigger.SCROLL_END]: "scroll_end",
  [BranchTrigger.NEXT_STEP]: "next_step",
};

/** «60%» shares of a weighted pool, whole and summing as the edge splits. Pure. */
export function poolShares(rows: { id: string; weight: number }[]): Map<string, number> {
  const total = rows.reduce((a, r) => a + r.weight, 0);
  return new Map(rows.map((r) => [r.id, total > 0 ? Math.round((r.weight / total) * 100) : 0] as const));
}

/**
 * Поток (доска CFlow): where a visitor of this campaign goes, in the order the
 * edge asks — the buyer's rules top to bottom with the visits each took, then
 * the shares of the offer and the landers with the people each got, the
 * exits a page can fire and the thank-you page. Read here; changed in the
 * campaign editor («Изменить правила», decision 06). The graph behind it is
 * not the buyer's: no graph editor and no link to /admin on this card.
 */
export function FlowTab({ ctx }: { ctx: CardContext }) {
  const t = useT();
  const tz = useReportTimezone();
  const p = useReportParams();
  const c = ctx.campaign;
  const offers = useOffersByIds(campaignOfferIds(c));
  const landers = useLandersByIds(campaignLanderIds(c));
  const offerName = (id: string) => offers.data?.get(id)?.name ?? id.slice(0, 8);
  const landerName = (id: string) => landers.data?.get(id)?.name ?? id.slice(0, 8);
  const managed = !!c.flowTemplateId;

  const base = { campaignId: c.id, from: p.from, to: p.to, tz: p.tz, basis: p.basis };
  const byNode = useCampaignReport({ ...base, dims: ["node"] }, managed && c.rules.length > 0);
  const byLander = useCampaignReport({ ...base, dims: ["landing"] });
  const byOffer = useCampaignReport({ ...base, dims: ["offer"] });
  const byBranch = useStreamBreakdown({ ...base, dimension: "branch" });
  const nodeVisits = new Map((byNode.data?.rows ?? []).map((r) => [r.dimensionPath[0] ?? "", statsRowToNumbers(r.stats).visits ?? 0] as const));
  const people = (rows: typeof byLander.data) => new Map((rows?.rows ?? []).map((r) => [r.dimensionPath[0] ?? "", statsRowToNumbers(r.stats)] as const));
  const landerNums = people(byLander.data);
  const offerNums = people(byOffer.data);

  const ruleHits = c.rules.map((r) => (r.nodeId ? (nodeVisits.get(r.nodeId) ?? 0) : undefined));
  const known = ruleHits.every((n) => n !== undefined);
  const rest = known && ctx.v?.visits !== undefined ? Math.max(0, ctx.v.visits - ruleHits.reduce<number>((a, n) => a + (n ?? 0), 0)) : undefined;

  const offerShare = poolShares(c.offerWeights.map((w) => ({ id: w.offerId, weight: w.weight })));
  const landerShare = poolShares(c.landerWeights.map((w) => ({ id: w.landerId, weight: w.weight })));
  const branches = [...EXITS, ...(c.branches.some((b) => b.trigger === BranchTrigger.NEXT_STEP) ? [BranchTrigger.NEXT_STEP] : [])];

  const target = (r: CampaignRule) =>
    r.offerId ? (
      <>
        <Badge variant="info">{t("card.flow.toOffer")}</Badge>
        <span className="truncate font-mono text-sm">{offerName(r.offerId)}</span>
      </>
    ) : r.landerId ? (
      <>
        <Badge variant="warn">{t("card.flow.toPage")}</Badge>
        <span className="truncate font-mono text-sm">{landerName(r.landerId)}</span>
      </>
    ) : (
      <>
        <Badge variant="neutral">{t("card.flow.toUrl")}</Badge>
        <span className="truncate font-mono text-sm" title={r.url}>
          {r.url.replace(/^https?:\/\//, "")}
        </span>
      </>
    );

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
      <div className="flex shrink-0 items-center gap-3">
        <p className="text-sm text-muted-foreground">{managed ? t("card.flow.lead") : t("card.flow.handBuilt")}</p>
        <div className="flex-1" />
        {managed ? (
          <WriteOnly>
            <Button asChild variant="secondary">
              <Link to={`/campaigns/${c.id}/edit#rules`}>
                <Pencil className="size-4" strokeWidth={1.5} /> {t("flow.edit")}
              </Link>
            </Button>
          </WriteOnly>
        ) : null}
      </div>
      <div className="grid grid-cols-1 gap-3 xl:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)]">
        <div className="flex min-w-0 flex-col gap-3">
          <Panel title={t("card.flow.rules")} aside={<Aside>{t("card.flow.rules.aside")}</Aside>}>
            {c.rules.length === 0 ? (
              <p className="px-3.5 py-4 text-sm text-muted-foreground">{t("flow.rules.none")}</p>
            ) : (
              <ol className="flex flex-col">
                {c.rules.map((r, i) => {
                  const cond = ruleCondition(t, r, tz);
                  return (
                    <li key={i} className="grid grid-cols-[--spacing(5)_minmax(0,1fr)_--spacing(4)_minmax(0,1.1fr)_--spacing(14)] items-center gap-3 border-b border-border px-3.5 py-2.5 text-sm">
                      <span className="font-mono text-xs text-muted-foreground">{i + 1}</span>
                      <span className="min-w-0">
                        {t("setup.rules.if")} <b className="font-semibold">{cond.lead}</b>
                        {cond.value ? <span className="ml-1 font-mono text-sm text-muted-foreground">{cond.value}</span> : null}
                      </span>
                      <ArrowRight className="size-4 text-muted-foreground" aria-hidden />
                      <span className="flex min-w-0 items-center gap-2">{target(r)}</span>
                      <span className="text-right font-mono tabular-nums">{byNode.isLoading ? "…" : formatInt(ruleHits[i])}</span>
                    </li>
                  );
                })}
                <li className="grid grid-cols-[--spacing(5)_minmax(0,1fr)_--spacing(14)] items-center gap-3 bg-surface-2 px-3.5 py-2.5 text-sm">
                  <span className="font-mono text-xs text-muted-foreground">{c.rules.length + 1}</span>
                  <span>
                    {t("card.flow.rest")} <b className="font-semibold">{t("card.flow.rest.shares")}</b>
                  </span>
                  <span className="text-right font-mono tabular-nums">{formatInt(rest)}</span>
                </li>
              </ol>
            )}
          </Panel>
          <Panel title={t("card.flow.exits")} aside={<Aside>{t("card.flow.exits.aside")}</Aside>}>
            <ul className="flex flex-col">
              {branches.map((trigger) => {
                const b = c.branches.find((x) => x.trigger === trigger);
                const shows = byBranch.data?.get(TRIGGER_KEY[trigger] ?? "")?.lander_shows;
                const share = poolShares([...(b?.landerWeights ?? []).map((w) => ({ id: `l:${w.landerId}`, weight: w.weight })), ...(b?.offerWeights ?? []).map((w) => ({ id: `o:${w.offerId}`, weight: w.weight }))]);
                const where = b
                  ? [...(b.landerWeights ?? []).map((w) => `${landerName(w.landerId)} · ${share.get(`l:${w.landerId}`)}%`), ...(b.offerWeights ?? []).map((w) => `${t("card.flow.offerDirect", { name: offerName(w.offerId) })} · ${share.get(`o:${w.offerId}`)}%`)].join(", ")
                  : "";
                return (
                  <li key={trigger} className="grid grid-cols-[--spacing(37.5)_minmax(0,1fr)_--spacing(14)] items-center gap-3 border-b border-border px-3.5 py-2.5 text-sm">
                    <span>{t((TRIGGER[trigger] ?? "card.flow.nextStep") as TKey)}</span>
                    <span className={cn("truncate font-mono text-sm", !b && "text-muted-foreground")}>{b ? where || DASH : t("card.flow.unset")}</span>
                    <span className="text-right font-mono tabular-nums">{b ? (byBranch.isLoading ? "…" : formatInt(shows ?? 0)) : DASH}</span>
                  </li>
                );
              })}
            </ul>
            <p className="px-3.5 py-2 text-xs text-muted-foreground">{t("card.flow.exits.note")}</p>
          </Panel>
        </div>
        <div className="flex min-w-0 flex-col gap-3">
          <Panel title={t("card.flow.offer")} aside={<Aside>{t("card.flow.offers.count", { n: c.offerWeights.length })}</Aside>}>
            <SharesTable
              loading={byOffer.isLoading}
              rows={c.offerWeights.map((w) => {
                const o = offers.data?.get(w.offerId);
                return {
                  id: w.offerId,
                  name: offerName(w.offerId),
                  sub: [o?.country.join(", "), ctx.item?.offers.find((x) => x.id === w.offerId)?.source].filter(Boolean).join(" · "),
                  pct: offerShare.get(w.offerId) ?? 0,
                  people: offerNums.get(w.offerId)?.clicks,
                };
              })}
            />
          </Panel>
          <Panel title={t("card.flow.landers")} aside={<Aside>{t("card.flow.landers.count", { n: c.landerWeights.length })}</Aside>}>
            <SharesTable
              loading={byLander.isLoading}
              rows={c.landerWeights.map((w) => {
                const l = landers.data?.get(w.landerId);
                const n = landerNums.get(w.landerId);
                return {
                  id: w.landerId,
                  name: landerName(w.landerId),
                  sub: [
                    l?.currentVersion ? `v${l.currentVersion}` : "",
                    l?.numberOfCtas ? t("card.flow.ctas", { n: l.numberOfCtas }) : "",
                    n?.cr !== undefined ? `CR ${formatPct(n.cr)}` : "",
                  ]
                    .filter(Boolean)
                    .join(" · "),
                  pct: landerShare.get(w.landerId) ?? 0,
                  people: n?.clicks,
                };
              })}
            />
            <div className="flex items-center gap-3 border-t border-border px-3.5 py-2.5 text-sm">
              <span className="text-muted-foreground">{t("card.flow.thanks")}</span>
              <span className="flex-1" />
              <span className="font-mono text-sm">{c.thankYouLanderId ? landerName(c.thankYouLanderId) : t("card.flow.thanks.default")}</span>
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}

function SharesTable({ rows, loading }: { rows: { id: string; name: string; sub: string; pct: number; people: number | undefined }[]; loading: boolean }) {
  const t = useT();
  if (rows.length === 0) return <p className="px-3.5 py-4 text-sm text-muted-foreground">{DASH}</p>;
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-border text-left text-label text-muted-foreground">
          <th className="px-3.5 py-2 font-medium">{t("card.flow.where")}</th>
          <th className="w-50 px-2 py-2 font-medium">{t("card.flow.share")}</th>
          <th className="w-18 px-3.5 py-2 text-right font-medium">{t("card.flow.people")}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id} className="border-b border-border last:border-b-0">
            <td className="max-w-0 px-3.5 py-2">
              <span className="block truncate">{r.name}</span>
              {r.sub ? <span className="block truncate text-xs text-muted-foreground">{r.sub}</span> : null}
            </td>
            <td className="px-2 py-2">
              <span className="flex items-center gap-3">
                <ProgressBar size="sm" value={r.pct} max={100} showPercent={false} className="flex-1" />
                <span className="w-10 text-right font-mono tabular-nums">{r.pct}%</span>
              </span>
            </td>
            <td className="px-3.5 py-2 text-right font-mono tabular-nums">{loading ? <Skeleton className="ml-auto h-4 w-10" /> : formatInt(r.people)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
